import { PlanningApplication, StatusDerived } from "../../types";
import { Nullable } from "src/js/types/Nullable";
import cn from "clsx";
import { getAppealColor, getStatusColor, PLANNING_TEXT_COLOR_MAP, PlanningColor } from "../../utils/colors";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { formatClassificationValue } from "../../utils/textFormatting";
import { AppealTag } from "../AppealTag";
import { Tag, TagColor } from "react-migration/components/Tag/Tag";

interface PlanningApplicationListItemProps {
  planningApplication: PlanningApplication;
  onClick?: (application: PlanningApplication) => void;
}

export function PlanningApplicationListItem({ planningApplication, onClick }: PlanningApplicationListItemProps) {
  const highlightColor =
    getAppealColor(planningApplication.appeal_decision) ||
    getStatusColor(planningApplication.status_derived) ||
    PlanningColor.DEFAULT;

  return (
    <section className="relative bg-white flex hover:bg-neutral-100 cursor-pointer">
      <div
        className={cn("w-full p-2 border-0 border-l-4 border-solid", {
          "border-green-400": highlightColor === PlanningColor.GREEN,
          "border-neutral-400": highlightColor === PlanningColor.GRAY,
          "border-red-500": highlightColor === PlanningColor.RED,
          "border-amber-500": highlightColor === PlanningColor.ORANGE,
          "border-b-blue-700": highlightColor === PlanningColor.DEFAULT,
        })}
      >
        <ItemBody planningApplication={planningApplication} />
        <ItemFooter
          reference={planningApplication.ref}
          status_derived={planningApplication.status_derived}
          decisionDate={planningApplication.decision_date}
        />
      </div>
      <button
        className="absolute inset-0 w-full"
        onClick={() => onClick?.(planningApplication)}
        title={planningApplication.title}
      />
    </section>
  );
}

const LINE_CLAMP = 3;

interface ItemBodyProps {
  planningApplication: PlanningApplication;
}

function ItemBody({ planningApplication }: ItemBodyProps) {
  const { classification, num_dwellings, title, appeal_decision: appealDecision, tags } = planningApplication;

  const { t } = useTranslation();

  return (
    <div>
      <div className="flex flex-wrap gap-1">
        <Tag tag={formatClassificationValue(classification)} color={TagColor.DEFAULT} />
        {appealDecision && <AppealTag appealDecision={appealDecision} />}

        {!!num_dwellings && <Tag tag={t("planning.dwelling_count", { count: num_dwellings })} />}

        {tags?.map((tag) => (
          <Tag key={tag} tag={tag} />
        ))}
      </div>
      <h1
        className={cn(
          "text-xs overflow-hidden text-ellipsis font-normal mb-1.5 mt-1.5",
          /** height = line height (1rem) * line clamp */
          { "max-h-12 line-clamp-3": LINE_CLAMP === 3 }
        )}
      >
        {title}
      </h1>
    </div>
  );
}

interface ItemFooterProps {
  reference: string;
  status_derived: Nullable<StatusDerived>;
  decisionDate: Nullable<string>;
}

function ItemFooter({ reference, status_derived, decisionDate }: ItemFooterProps) {
  const statusColor = getStatusColor(status_derived) || PlanningColor.DEFAULT;

  return (
    <div className="flex text-xs text-neutral-500 gap-x-2">
      <div className={PLANNING_TEXT_COLOR_MAP[statusColor]}>{status_derived}</div>
      <div>{reference}</div>
      {decisionDate && <div className="ml-auto"> Received {new Date(decisionDate).toLocaleDateString()}</div>}
    </div>
  );
}
